import {ErrorCode} from '@sollapay/enums';
import {Button, ErrorState, Illustration} from '@sollapay/ui/components';
import {
  DrawHtmlIllustration,
  SearchIllustration,
  SecureShieldIllustration
} from '@sollapay/ui/illustrations';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {getErrorCode} from '@/utils';

import type {FC, ReactNode} from 'react';

export interface PageErrorStateProps {
  error?: unknown;
  refetch?: () => void;
}

interface ErrorStateVariant {
  illustration: ReactNode;
  titleKey: string;
  subtitleKey: string;
  /** `retry` shows a retry button when the page passes `refetch`; null shows no button. */
  action: 'retry' | ReactNode;
}

const DEFAULT_VARIANT: ErrorStateVariant = {
  illustration: <DrawHtmlIllustration />,
  titleKey: 'errorState.title',
  subtitleKey: 'errorState.subtitle',
  action: 'retry'
};

/** Add an entry here for any error code that should get its own illustration/copy — everything else falls back to `DEFAULT_VARIANT`. */
const ERROR_CODE_VARIANTS: Partial<Record<ErrorCode, ErrorStateVariant>> = {
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: {
    illustration: <SecureShieldIllustration />,
    titleKey: 'errorState.insufficientPermissions.title',
    subtitleKey: 'errorState.insufficientPermissions.subtitle',
    action: 'retry'
  },
  [ErrorCode.TRUST_ACCOUNT_NOT_FOUND]: {
    illustration: <SearchIllustration />,
    titleKey: 'errorState.notFound.title',
    subtitleKey: 'errorState.notFound.subtitle',
    action: null
  },
  [ErrorCode.GUEST_ACCESS_REMOVED]: {
    illustration: <SecureShieldIllustration />,
    titleKey: 'errorState.guestAccessRemoved.title',
    subtitleKey: 'errorState.guestAccessRemoved.subtitle',
    action: <BackToSharedButton />
  }
};

export const PageErrorState: FC<PageErrorStateProps> = ({error, refetch}) => {
  const {t} = useTranslation('common');
  const code = getErrorCode(error);
  const variant = ERROR_CODE_VARIANTS[code as ErrorCode] ?? DEFAULT_VARIANT;

  return (
    <div className="flex min-h-150 items-center justify-center">
      <ErrorState
        illustration={<Illustration width={166}>{variant.illustration}</Illustration>}
        title={t(variant.titleKey)}
        subtitle={t(variant.subtitleKey)}
        action={
          variant.action === 'retry'
            ? refetch && (
                <Button variant="secondary" size="sm" onClick={refetch}>
                  {t('errorState.retry')}
                </Button>
              )
            : variant.action
        }
      />
    </div>
  );
};

/** Only a guest page raises `GUEST_ACCESS_REMOVED`, so this always renders inside the router. */
function BackToSharedButton() {
  const {t} = useTranslation('common');
  const navigate = useNavigate();

  return (
    <Button variant="secondary" size="sm" onClick={() => void navigate('/shared')}>
      {t('errorState.guestAccessRemoved.backToShared')}
    </Button>
  );
}
